/**
 * English content for the demo's domain data, keyed by id.
 *
 * Kept OUT of the data files so `skills.ts`, `bausteine.ts`, `roles.ts` and
 * `obligations.ts` stay monolingual and framework-free — localisation is a
 * presentation concern, exactly like the rationale wording.
 *
 * Swiss regulatory terms use their official English names where one exists
 * (FIDLEG → FinSA, GwG → AMLA, VSB → CDB, revDSG → revFADP); the German
 * abbreviation is kept in parentheses where an audience would look for it,
 * because these are the names on the actual training records.
 */

// ============================================================================
// Skills
// ============================================================================

export const SKILL_LABELS_EN: Record<string, string> = {
    // Regulatory
    "reg.fidleg": "FinSA / conduct rules",
    "reg.suitability": "Appropriateness & suitability assessment",
    "reg.gwg": "Anti-money laundering (AMLA)",
    "reg.vsb": "Due diligence obligations (CDB)",
    "reg.kyc": "Know Your Customer",
    "reg.datenschutz": "Data protection (FADP)",
    "reg.bankkundengeheimnis": "Bank client confidentiality",
    "reg.sanktionen": "Sanctions & embargoes",
    "reg.marktverhalten": "Market conduct rules",
    "reg.opRisk": "Operational risk",
    "reg.infosec": "Information security",
    "reg.bcm": "Business continuity management",
    "reg.outsourcing": "Outsourcing (FINMA Circ. 18/3)",

    // Professional
    "fach.anlageberatung": "Investment advisory",
    "fach.portfolio": "Portfolio construction",
    "fach.wertschriften": "Securities & instruments",
    "fach.vorsorge": "Pensions (pillars 2 & 3)",
    "fach.steuern": "Retail client taxation",
    "fach.hypotheken": "Mortgage business",
    "fach.tragbarkeit": "Affordability & loan-to-value",
    "fach.kreditanalyse": "Credit analysis",
    "fach.kreditrisiko": "Credit risk assessment",
    "fach.firmenkunden": "Corporate banking",
    "fach.bilanzanalyse": "Financial statement analysis",
    "fach.esg": "Sustainability & ESG",
    "fach.esgBeratung": "ESG in client advisory",
    "fach.zahlungsverkehr": "Payments",
    "fach.produktkenntnis": "Acme Bank product knowledge",
    "fach.beratungsprozess": "Advisory process",
    "fach.gespraechsfuehrung": "Client conversation skills",
    "fach.einwandbehandlung": "Handling objections",
    "fach.finanzplanung": "Holistic financial planning",
    "fach.immobilienbewertung": "Property valuation",
    "fach.recovery": "Restructuring & recovery",

    // Leadership
    "fue.feedback": "Giving & receiving feedback",
    "fue.mag": "Conducting performance reviews",
    "fue.konflikt": "Conflict management",
    "fue.delegation": "Delegation & prioritisation",
    "fue.rekrutierung": "Recruiting & selection",
    "fue.teamentwicklung": "Team development",
    "fue.changeManagement": "Change management",
    "fue.arbeitsrecht": "Employment law for managers",
    "fue.absenzen": "Absence & case management",

    // Digital
    "dig.aiGrundlagen": "AI fundamentals",
    "dig.aiRisiken": "Risks of generative AI",
    "dig.promptEngineering": "Prompt engineering",
    "dig.datenanalyse": "Data analysis",
    "dig.excel": "Excel for analysis",
    "dig.digitaleKanaele": "Digital client channels",
    "dig.cloudGrundlagen": "Cloud fundamentals",
    "dig.itArchitektur": "IT architecture",
    "dig.softwareEngineering": "Software engineering",
    "dig.securityEngineering": "Security engineering",

    // Method
    "met.projektmanagement": "Project management",
    "met.agile": "Agile ways of working",
    "met.prozessmanagement": "Process management",
    "met.praesentation": "Presenting & facilitation",
    "met.verhandlung": "Negotiation",
    "met.schreiben": "Written communication",
    "met.selbstorganisation": "Self-organisation",
}

// ============================================================================
// Roles
// ============================================================================

export const ROLE_LABELS_EN: Record<string, string> = {
    kundenberater: "Retail Client Advisor",
    anlageberater: "Investment Advisor",
    finanzplaner: "Financial Planner",
    hypothekarberater: "Mortgage Advisor",
    kreditanalyst: "Credit Analyst",
    firmenkundenberater: "Corporate Client Advisor",
    "compliance-officer": "Compliance Officer",
    "risk-manager": "Risk Manager",
    "it-engineer": "IT Engineer",
    "it-security": "Security Engineer",
    projektleiter: "Project Manager",
    operations: "Operations Specialist",
    "hr-specialist": "HR Specialist",
    teamleiter: "Team Lead",
    lernender: "Apprentice / Trainee",
}

export const ASPIRATION_LABELS_EN: Record<string, string> = {
    none: "No goal stated",
    teamleitung: "Move into team leadership",
    finanzplanung: "Towards financial planning",
    kreditspezialist: "Become a credit specialist",
    projektleitung: "Towards project management",
    anlagespezialist: "Become an investment specialist",
    nachhaltigkeit: "Towards sustainability and ESG",
    compliance: "Towards compliance",
    datenanalyse: "Towards data and analysis",
}

// ============================================================================
// Obligations
// ============================================================================

export const OBLIGATION_EN: Record<string, { label: string; basis: string }> = {
    "obl.fidleg": {
        label: "FinSA training duty for client advisors",
        basis: "FinSA Art. 6 / FinSO — training and continuing education duty",
    },
    "obl.gwg": {
        label: "Anti-money laundering training duty",
        basis: "AMLA Art. 8 / FINMA-AMLO — staff training",
    },
    "obl.vsb": {
        label: "CDB due diligence obligations",
        basis: "CDB 20 — due diligence when entering into business relationships",
    },
    "obl.datenschutz": {
        label: "Data protection training",
        basis: "revFADP — staff awareness",
    },
    "obl.infosec": {
        label: "Information security training",
        basis: "FINMA Circ. 23/1 Operational Risks — awareness",
    },
    "obl.ai": {
        label: "Mandatory generative AI course",
        basis: "Directive A-035 Terms of use for generative AI",
    },
    "obl.sanktionen": {
        label: "Sanctions training",
        basis: "Embargo Act / SECO — internal sanctions directive",
    },
    "obl.kreditkompetenz": {
        label: "Credit authority training",
        basis: "Internal credit authority framework — prerequisite for signing authority",
    },
    "obl.arbeitsrecht": {
        label: "Employment law for managers",
        basis: "Internal leadership directive — mandatory on taking up a leadership role",
    },
}

export const SIGNING_AUTHORITY_EN: Record<string, string> = {
    "Kreditkompetenz A": "Credit authority A",
    "Kreditkompetenz B": "Credit authority B",
}

// ============================================================================
// Bausteine
// ============================================================================

export const BAUSTEIN_EN: Record<string, { title: string; description: string }> = {
    // — Regulatory —
    "fidleg-basis": {
        title: "FinSA: fundamentals and conduct rules",
        description:
            "Duties under the Financial Services Act: information, documentation and accountability duties, client segmentation, conflicts of interest.",
    },
    "fidleg-refresher": {
        title: "FinSA refresher for client advisors",
        description:
            "Annual refresher: cases from advisory practice, changes to directives and supervisory practice, common internal audit findings.",
    },
    suitability: {
        title: "Appropriateness and suitability assessment in practice",
        description:
            "Which assessment applies when, how the risk profile is captured and documented, handling client wishes outside the profile.",
    },
    "gwg-basis": {
        title: "Anti-money laundering: fundamentals",
        description:
            "AMLA duties, risk indicators, reporting to the Money Laundering Reporting Office, role of the AML unit.",
    },
    "gwg-refresher": {
        title: "AML refresher with case studies",
        description:
            "Annual refresher based on anonymised cases from the bank, current typologies and MROS practice.",
    },
    "vsb-kyc": {
        title: "CDB and KYC: applying due diligence correctly",
        description:
            "Identifying the contracting party, establishing the beneficial owner, documentation requirements, subsequent corrections.",
    },
    datenschutz: {
        title: "Data protection (revFADP) in banking",
        description:
            "Processing principles, right of access, reporting data security breaches, handling client data in third-party systems.",
    },
    "infosec-basis": {
        title: "Information security for all staff",
        description:
            "Phishing, passwords and MFA, clean desk, reporting security incidents, information classification.",
    },
    sanktionen: {
        title: "Sanctions and embargoes",
        description:
            "SECO lists, screening processes, handling hits, escalation paths where sanctions are involved.",
    },
    "op-risk": {
        title: "Operational risk and internal control system",
        description:
            "Risk inventory, key controls, loss data collection, interplay with internal audit.",
    },
    bcm: {
        title: "Business continuity management: roles and incidents",
        description:
            "BCM organisation, critical processes, recovery plans, exercises and escalation paths during outages.",
    },
    outsourcing: {
        title: "Managing outsourcing (FINMA Circ. 18/3)",
        description:
            "Materiality assessment, outsourcing inventory, requirements on service providers, ongoing monitoring.",
    },

    // — Advisory & investment —
    beratungsprozess: {
        title: "The Acme Bank advisory process",
        description:
            "From needs analysis to follow-up: structure, tools and documentation of the advisory conversation.",
    },
    gespraechsfuehrung: {
        title: "Conversation skills and handling objections",
        description:
            "Questioning technique, active listening, handling objections and price discussions, closing orientation.",
    },
    "anlageberatung-basis": {
        title: "Investment advisory: fundamentals",
        description:
            "Asset classes, risk and return, Acme Bank investment strategies, role of the investment committee.",
    },
    portfoliokonstruktion: {
        title: "Portfolio construction and rebalancing",
        description:
            "Strategic and tactical allocation, diversification, costs, systematic rebalancing in client portfolios.",
    },
    "esg-grundlagen": {
        title: "Sustainability and ESG: fundamentals",
        description:
            "ESG criteria, investment approaches, greenwashing risks, the Swiss regulatory framework.",
    },
    "esg-beratung": {
        title: "ESG in the client conversation",
        description:
            "Capturing and documenting sustainability preferences, explaining ESG products, answering critical client questions.",
    },
    "vorsorge-basis": {
        title: "Swiss pensions: pillars 2 and 3",
        description:
            "The three-pillar system, buying into the pension fund, pillar 3a/3b, early withdrawal for home ownership.",
    },
    finanzplanung: {
        title: "Holistic financial planning",
        description:
            "Life-stage planning, retirement planning, estate and matrimonial property law, interplay with taxation.",
    },
    "steuern-privatkunden": {
        title: "Taxation for retail clients",
        description:
            "Income and wealth tax, deductions, tax effects of pension and investment decisions.",
    },

    // — Credit & financing —
    "hypotheken-basis": {
        title: "Mortgage business: fundamentals",
        description:
            "Products and terms, interest rate models, loan-to-value and amortisation, how a financing case runs.",
    },
    tragbarkeit: {
        title: "Affordability and loan-to-value in practice",
        description:
            "Imputed interest rate, own funds, second homes, affordability calculations for the self-employed.",
    },
    immobilienbewertung: {
        title: "Property valuation",
        description:
            "Hedonic models, intrinsic and capitalised earnings value, sanity-checking valuations, special properties.",
    },
    "kreditanalyse-basis": {
        title: "Credit analysis: fundamentals",
        description:
            "Credit application, creditworthiness assessment, collateral, authority framework and approval path.",
    },
    bilanzanalyse: {
        title: "Balance sheet and annual accounts analysis",
        description:
            "Key ratios, cash flow analysis, adjustments, assessing the earnings power of SMEs.",
    },
    "kreditrisiko-fortgeschritten": {
        title: "Advanced credit risk assessment",
        description:
            "Rating models, concentration risk, early warning indicators, a portfolio view of the loan book.",
    },
    firmenkunden: {
        title: "Corporate banking",
        description:
            "SME needs, operating credit, leasing, succession planning, interplay with payments.",
    },
    recovery: {
        title: "Restructuring and recovery",
        description:
            "Early warning, restructuring concepts, debt enforcement and realisation, working with legal.",
    },
    zahlungsverkehr: {
        title: "Payments, domestic and international",
        description:
            "SIC, SEPA, ISO 20022, QR-bill, instant payments, processing and exception handling.",
    },

    // — Leadership —
    "fuehrung-basis": {
        title: "Leading: fundamentals for new managers",
        description:
            "The shift from expert to leader, the Acme Bank leadership model, setting expectations in the team.",
    },
    feedback: {
        title: "Giving and receiving feedback",
        description:
            "Effective everyday feedback, difficult messages, handling defensive reactions.",
    },
    "mag-fuehren": {
        title: "Conducting effective performance reviews",
        description:
            "Preparation, objective setting, assessment, development planning and documentation in the review cycle.",
    },
    konflikt: {
        title: "Conflict management in the team",
        description:
            "Recognising conflict types, escalation levels, facilitating conflict conversations, the role of HR.",
    },
    rekrutierung: {
        title: "Recruiting and structured selection",
        description:
            "Requirement profile, structured interview, assessment bias, deciding in the selection panel.",
    },
    arbeitsrecht: {
        title: "Employment law for managers",
        description:
            "Employment contract, working hours, termination, absence and illness, the employer's duty of care.",
    },
    change: {
        title: "Change management for managers",
        description:
            "The change curve, communicating under uncertainty, handling resistance, involving the team.",
    },

    // — Digital / AI —
    "ai-risiko": {
        title: "Risk-aware use of generative AI",
        description:
            "Mandatory course on using generative AI: data protection, bank client confidentiality, hallucinations, Directive A-035.",
    },
    "ai-grundlagen": {
        title: "AI foundations training",
        description:
            "How generative AI works, what it can and cannot do, sensible applications in day-to-day banking.",
    },
    prompting: {
        title: "Prompt engineering for everyday work",
        description:
            "Effective prompts, working with instructions and documents, limits and the duty to verify results.",
    },
    "excel-analysen": {
        title: "Excel for analysis",
        description:
            "Pivot tables, Power Query, clean data models, common sources of error in analyses.",
    },
    datenanalyse: {
        title: "Data analysis for business units",
        description:
            "From question to analysis, defining metrics cleanly, visualisation and interpretation.",
    },
    "cloud-grundlagen": {
        title: "Cloud fundamentals (Azure)",
        description:
            "Service models, landing zones, cost and governance, the bank's security requirements.",
    },
    "security-engineering": {
        title: "Secure software engineering",
        description:
            "OWASP Top 10, secrets management, threat modelling, secure dependencies and code reviews.",
    },
    "it-architektur": {
        title: "Acme Bank IT architecture",
        description:
            "System landscape, integration patterns, architecture principles and governance in the architecture board.",
    },
    "digitale-kanaele": {
        title: "Digital client channels",
        description:
            "E-banking and app from the client's perspective, onboarding journeys, support cases and channel switches in advisory.",
    },

    // — Method —
    projektmanagement: {
        title: "Project management fundamentals",
        description:
            "Project mandate, planning, risks and stakeholders, steering and reporting in the project portfolio.",
    },
    agile: {
        title: "Agile ways of working",
        description:
            "Scrum and Kanban in practice, roles, backlog work, interplay with classic project management.",
    },
    praesentation: {
        title: "Presenting and facilitation",
        description:
            "Structuring convincing presentations, handling nerves, facilitating meetings and workshops.",
    },
    verhandlung: {
        title: "Negotiation",
        description:
            "Preparation, interests over positions, handling price pressure, closing and follow-up.",
    },
    selbstorganisation: {
        title: "Self-organisation and prioritisation",
        description:
            "Task management, handling interruptions, focus time, collaboration in a hybrid setup.",
    },
    // — On-the-job and social learning (the 70-20-10 "70" and "20") —
    "hospitation-kreditcenter": {
        title: "Placement: corporate credit centre",
        description:
            "Two days in the corporate credit centre: reading live files, sitting in on applications, attending a credit committee. Concludes with an assessment of a case of your own.",
    },
    "mentoring-beratung": {
        title: "Advisory mentoring",
        description:
            "Six months of mentoring by an experienced advisor: a monthly conversation, two accompanied client meetings, and a joint debrief of each.",
    },
    "lernpartnerschaft-vorsorge": {
        title: "Pensions learning partnership",
        description:
            "Paired with a colleague from pensions advisory: four joint case reviews, mutual shadowing, and one client example worked through together.",
    },
    "fachzirkel-esg": {
        title: "Sustainability practice circle",
        description:
            "A monthly exchange on ESG in advisory work: current regulation, new products, and the difficult client questions participants have actually met.",
    },
    "projektmitarbeit-digital": {
        title: "Project work: digitalisation",
        description:
            "A quarter contributing to a live digitalisation project: bringing the client perspective into requirements, supporting testing, presenting the outcome to the team.",
    },
    "shadowing-fuehrung": {
        title: "Leadership shadowing",
        description:
            "Two weeks alongside a manager: team meetings, one performance conversation as an observer, and a weekly reflection.",
    },
    "kollegiale-fallberatung": {
        title: "Peer case consultation",
        description:
            "Structured case consultation in a standing group of six: bring your own difficult advisory situations and work them through to a fixed method.",
    },
    "stellvertretung-teamleitung": {
        title: "Standing in as team lead",
        description:
            "Cover the team lead during an absence: run the daily business, chair the team meeting, decide escalations. With a briefing before and a debrief after.",
    },
    "praxisauftrag-zahlungsverkehr": {
        title: "Practical assignment: payments",
        description:
            "Take a real process case in payments, analyse it independently, and argue an improvement proposal in front of the specialist team.",
    },
    "peer-review-kreditantraege": {
        title: "Peer review of credit applications",
        description:
            "Mutual four-eyes review of credit applications over a quarter, with documented feedback on the quality of reasoning and conditions.",
    },

    // — Added with the catalogue expansion: regulatory e-learning —
    "kyc-basis": {
        title: "KYC: fundamentals and practice",
        description:
            "Identification and verification, beneficial owners, PEP checks and ongoing monitoring of the business relationship.",
    },
    "vsb-sorgfaltspflichten": {
        title: "CDB due diligence in practice",
        description:
            "Forms, documentation duties and the findings that come up most often in a CDB audit, with cases from day-to-day business.",
    },
    "marktverhalten-basis": {
        title: "Market conduct rules",
        description:
            "Inside information, market manipulation, staff personal account dealing and reporting duties towards the supervisor.",
    },
    bankkundengeheimnis: {
        title: "Bank client confidentiality",
        description:
            "Scope and limits of bank client confidentiality, administrative and mutual legal assistance, disclosure to third parties and how to handle enquiries.",
    },
    "suitability-elearning": {
        title: "Appropriateness and suitability: e-learning",
        description:
            "Which assessment applies when, how the risk profile is captured and documented — a compact refresher between classroom dates.",
    },
    "bcm-basis": {
        title: "Business continuity: fundamentals",
        description:
            "Critical processes, recovery times, the emergency organisation and your own role when an incident happens.",
    },
    "produktkenntnis-refresher": {
        title: "Product knowledge: annual refresher",
        description:
            "What changed in the product range, adjusted conditions and withdrawn products — compact, with a knowledge check.",
    },
    "excel-grundlagen": {
        title: "Excel: fundamentals for analysis",
        description:
            "Pivot tables, lookup functions and clean data preparation for the analyses you run again and again.",
    },
    "selbstorganisation-basis": {
        title: "Self-organisation and prioritisation",
        description:
            "Batch work, justify priorities and keep commitments realistic — with templates for weekly planning and handovers.",
    },
    "schreiben-kundenkorrespondenz": {
        title: "Written client correspondence",
        description:
            "Write clearly, translate technical terms, and deliver difficult messages properly in writing.",
    },

    // — Added with the catalogue expansion: on the job and social learning —
    "hospitation-anlagedesk": {
        title: "Placement: investment desk",
        description:
            "Three days in the investment centre: read investment proposals, sit in on the investment committee, and defend your own model portfolio in the team.",
    },
    "hospitation-compliance-desk": {
        title: "Placement: compliance desk",
        description:
            "Two days with compliance: follow suspicious activity checks, read along with transaction monitoring, and document a case of your own.",
    },
    "praxisauftrag-finanzplanung": {
        title: "Practical assignment: holistic financial planning",
        description:
            "Produce a full financial plan for a real client situation, have it reviewed under four eyes, and present it to the client.",
    },
    "job-rotation-operations": {
        title: "Job rotation: operations",
        description:
            "Four weeks in processing: payments, rework and error patterns seen from the processing side rather than the advisory side.",
    },
    "hospitation-recovery": {
        title: "Placement: restructuring and recovery",
        description:
            "Two days in restructuring: follow non-performing exposures, weigh up recovery options, and reflect on advisory's role upstream.",
    },
    "portfolio-uebernahme-begleitet": {
        title: "Supported portfolio handover",
        description:
            "Take over an existing client portfolio over three months with support: first meetings, handover documentation and a position statement per relationship.",
    },
    "praxisauftrag-immobilien": {
        title: "Practical assignment: property valuation",
        description:
            "Value three properties independently, justify the valuation, and reconcile it against the specialist unit's assessment.",
    },
    teilprojektleitung: {
        title: "Take on a sub-project lead",
        description:
            "Run a scoped sub-project for a quarter: planning, status reports, risks, and sign-off by the project lead.",
    },
    "mentoring-fuehrungsnachwuchs": {
        title: "Mentoring for emerging leaders",
        description:
            "Six months of mentoring by an experienced manager: a monthly conversation, support through two team situations, and a joint review.",
    },
    "intervision-fuehrung": {
        title: "Peer supervision: leadership topics",
        description:
            "A fixed group of managers brings its own cases over six months and works them through to a set format.",
    },
    "community-of-practice-kredit": {
        title: "Community of practice: credit",
        description:
            "Monthly exchange on borderline cases and new directives in lending, with a rotating case presentation from your own portfolio.",
    },
    "lunch-and-learn-regulatorik": {
        title: "Lunch and learn: regulatory update",
        description:
            "A short format over lunch: what changed in directives and supervisory practice, and what it concretely means for advisory.",
    },
    "shadowing-kundengespraeche": {
        title: "Shadowing client conversations",
        description:
            "Sit in on eight client conversations run by an experienced advisor, with a structured debrief after each one.",
    },
    "reverse-mentoring-digital": {
        title: "Reverse mentoring: digital",
        description:
            "Younger colleagues coach experienced ones on digital tools — four sessions built around real use cases of their own.",
    },
    "lernpartnerschaft-steuern": {
        title: "Learning partnership: tax",
        description:
            "Two advisors work through tax cases from their own client books together over a quarter and draw a joint conclusion.",
    },
    "fachzirkel-datenanalyse": {
        title: "Specialist circle: data analysis",
        description:
            "Five sessions in which participants present their own analyses, review each other's, and sharpen the method together.",
    },
}

export const PROVIDER_EN: Record<string, string> = {
    "Compliance": "Compliance",
    "Legal & Compliance": "Legal & Compliance",
    "Information Security": "Information Security",
    "Risk Management": "Risk Management",
    "IT Risk & BCM": "IT Risk & BCM",
    Vertriebsausbildung: "Sales Academy",
    "Investment Center": "Investment Center",
    Kreditausbildung: "Credit Academy",
    Operations: "Operations",
    "HR Entwicklung": "HR Development",
    "HR / Legal": "HR / Legal",
    "learn.acme-bank.ch": "learn.acme-bank.ch",
    "Data & Automation": "Data & Automation",
    "Information Technology": "Information Technology",
    "Digital Banking": "Digital Banking",
    PMO: "PMO",
    "Kreditcenter Firmenkunden": "Corporate Credit Centre",
    "Fachstelle Vorsorge": "Pensions Practice",
    "Fachstelle Nachhaltigkeit": "Sustainability Practice",
}

/**
 * The learning frameworks.
 *
 * These live here rather than in `ui.ts` because they are DOMAIN CONTENT keyed
 * by id, like the Bausteine and obligations — not interface chrome. The
 * trade-off is that `ui.ts` is compile-enforced (EN is
 * `Record<keyof typeof de, string>`) while this file is a plain record with a
 * silent German fallback, which is exactly how six framework cards shipped
 * untranslated. `tests/unit/modules/lernpfad/i18n-completeness.test.ts` closes
 * that hole for every id-keyed record in this file.
 */
export const FRAMEWORK_EN: Record<
    string,
    { name: string; claim: string; changes: string[]; watch: string }
> = {
    kirkpatrick: {
        name: "Kirkpatrick (New World)",
        claim: "Four levels — reaction, learning, behaviour, results — planned backwards: settle the outcome first, then design the programme.",
        changes: [
            "A completion alone no longer evidences a duty; at least a passed knowledge check is required.",
            "Level 1 measures the intention to apply, not satisfaction — target 4.0 out of 5.0.",
            "Level 2 counts as passed from 66%.",
            "Level 3 is confirmed by the line manager 14–30 days after completion, with the four required drivers.",
            "Level 4 is read 60–90 days after that.",
        ],
        watch: "Duty coverage",
    },
    phillips: {
        name: "Phillips ROI (Level 5)",
        claim: "Kirkpatrick plus a fifth level: benefit and cost in dollars, with the programme effect isolated.",
        changes: [
            "Ranking is denominated in dollars rather than hours — value per dollar instead of value per hour.",
            "The rate card per format becomes active (classroom USD 150/h plus USD 100 fixed, webinar 60, e-learning 25).",
            "Learners' working time is counted at USD 85/h as an opportunity cost.",
            "Expensive classroom formats lose to cheap webinars as soon as they close the same gap.",
        ],
        watch: "Programme cost",
    },
    ltem: {
        name: "LTEM",
        claim: "Eight tiers instead of four. Attendance and knowledge checks evidence no learning — only decision-making and task competence count.",
        changes: [
            "Competence is credited only where a decision test or transfer evidence exists.",
            "A passed knowledge check no longer counts as proof of competence (LTEM tier 4 rather than 5).",
            "Gaps that were closed reopen — the plan grows without a single person having changed.",
            "Duty coverage is left untouched: LTEM judges competence, not compliance.",
        ],
        watch: "Planned hours",
    },
    brinkerhoff: {
        name: "Success Case Method",
        claim: "The average is not the point; the outliers are — who applied it, who did not, and what made the difference.",
        changes: [
            "Competence is credited only where use on the job was confirmed — regardless of any test result.",
            "Self-report is enough; what matters is that somebody looked at all.",
            "Makes visible how much of the catalogue was never confirmed: the non-success cases are the actual finding.",
        ],
        watch: "Confirmed transfer",
    },
    "seventy-twenty-ten": {
        name: "70-20-10",
        claim: "70% of development happens at work, 20% through others, 10% in a course — a plan should reflect that.",
        changes: [
            "The composer steers towards a 70 / 20 / 10 modality mix.",
            "Placements, mentoring, peer circles and practical assignments move ahead of formal courses while their modality is under-represented.",
            "Not a quota: a needed course is never dropped to hit a percentage — ranking still decides.",
        ],
        watch: "Modality mix",
    },
    spacing: {
        name: "Spacing effect",
        claim: "The forgetting curve is the oldest robust finding in learning research — and the one course catalogues ignore most reliably.",
        changes: [
            "A short booster is scheduled 30 days after every mandatory course.",
            "The booster is not a course but a scheduled act of retrieval — one hour, self-study.",
            "It is scheduled only where the quarterly budget carries it: a booster that displaces a duty defeats its own purpose.",
        ],
        watch: "Quarterly load",
    },
}
